import { Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RealtimeService } from '@examdojo/supabase';
import { catchError, filter, forkJoin, map, merge, Observable, of, switchMap, tap, timer } from 'rxjs';
import { distinctUntilArrayChanged } from '@examdojo/core/rxjs';
import { keys } from '@examdojo/core/util/object-utils';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { ImageChatMessageQuery, ImageChatMessageStore } from '../store';
import { ImageIndexToChatIdProps } from '../models';
import { ImageChatMessageService } from './image-chat-message.service';

export interface ImageChatLoadEventPayload {
  new_message_ids: number[];
  request_id: string;
}

export const ImageChatEventName = 'new_messages';

export function getImageChatChannelName(chatId: number) {
  return `chat:${chatId}`;
}

@Injectable()
export class ImageChatRealtimeService {
  constructor(
    private readonly realtimeService: RealtimeService,
    private readonly imageChatMessageStore: ImageChatMessageStore,
    private readonly imageChatMessageQuery: ImageChatMessageQuery,
    private readonly imageChatMessageService: ImageChatMessageService,
  ) {
    this.listenForStorePropsChanges()
      .pipe(
        switchMap((chatIdsWithRequestId) => this.listenForChatChannels(chatIdsWithRequestId)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  listenForStorePropsChanges(): Observable<Array<ImageIndexToChatIdProps & { requestId: string }>> {
    return this.imageChatMessageQuery.selectProp('imageIndexToChatId').pipe(
      filter(Boolean),
      map(
        (imageIndexToChatId) =>
          Object.values(imageIndexToChatId).filter(
            (chat) => isNotNullish(chat.requestId) && chat.requestId !== 'progressive-ui-temporary-request-id',
          ) as Array<ImageIndexToChatIdProps & { requestId: string }>,
      ),
      distinctUntilArrayChanged(),
    );
  }

  listenForChatChannels(chatIdsWithRequestId: Array<ImageIndexToChatIdProps & { requestId: string }>) {
    return forkJoin(
      chatIdsWithRequestId.map(({ chatId, requestId }) =>
        merge(this.listenForChatChannel(chatId), this.fetchMessageForRequestOnTimer(requestId, chatId)),
      ),
    );
  }

  private listenForChatChannel(chatId: number) {
    return this.realtimeService
      .listenBroadcastRealtime<ImageChatLoadEventPayload>(
        [{ event: ImageChatEventName }],
        getImageChatChannelName(chatId),
      )
      .pipe(
        switchMap((eventPayload) => {
          if (!eventPayload) {
            return of(null);
          }

          return forkJoin(
            (eventPayload.payload.new_message_ids ?? []).map((messageId) => {
              if (this.imageChatMessageStore.hasEntity(messageId)) {
                return of(null);
              }

              return this.imageChatMessageService
                .fetchMessageByIds([messageId])
                .pipe(
                  tap(
                    (model) =>
                      model && this.updateImagePropsWithFinishedRequestId(eventPayload.payload.request_id, chatId),
                  ),
                );
            }),
          );
        }),
      );
  }

  private fetchMessageForRequestOnTimer(requestId: string, chatId: number, timeout = 10_000) {
    return timer(timeout, timeout).pipe(
      switchMap(() =>
        this.imageChatMessageService.fetchMessageByRequestId(requestId, chatId).pipe(catchError(() => of(null))),
      ),
      tap((models) => models && models.length && this.updateImagePropsWithFinishedRequestId(requestId, chatId)),
    );
  }

  private updateImagePropsWithFinishedRequestId(requestId: string, chatId: number) {
    const imageIndexToUpdate = keys(this.imageChatMessageQuery.getProp('imageIndexToChatId')).find(
      (imageIndex) =>
        this.imageChatMessageQuery.getProp('imageIndexToChatId')[imageIndex].requestId === requestId &&
        this.imageChatMessageQuery.getProp('imageIndexToChatId')[imageIndex].chatId === chatId,
    );

    if (!imageIndexToUpdate) {
      return;
    }

    return this.imageChatMessageService.updateImagePropsWithStartedRequestId(null, imageIndexToUpdate);
  }
}
